import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reads and writes a group of query-string parameters as one object.
 *
 * List screens keep search, filters, sort and page in the URL rather than in
 * component state so that a view is shareable, survives a refresh, and — the
 * part local state gets wrong most often — restores correctly when the user
 * opens a record and presses Back.
 *
 * Values are always strings, matching what a URL can carry; callers coerce at
 * the edges. Writing `undefined` or `''` removes the key, so the URL only ever
 * carries the parameters that are actually set instead of a trail of empties.
 */
export type UrlState<K extends string> = Partial<Record<K, string>>;

export interface UseUrlStateOptions<K extends string> {
  /**
   * Values that equal their default are stripped from the URL, keeping the
   * common case a clean path. Reading still returns the default.
   */
  defaults?: Partial<Record<K, string>>;
  /**
   * Keys reset whenever any *other* key changes — page numbers, typically:
   * changing a filter invalidates the current page.
   */
  resetOnChange?: K[];
  /**
   * Replaces the history entry instead of pushing one. Use for high-frequency
   * updates (typing) so Back doesn't have to walk through every keystroke.
   */
  replace?: boolean;
}

export function useUrlState<K extends string>(
  keys: readonly K[],
  options: UseUrlStateOptions<K> = {},
) {
  const {
    defaults = {} as Partial<Record<K, string>>,
    resetOnChange = [],
    replace = true,
  } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Serialise for the dependency array: URLSearchParams is a fresh object on
  // every render, so comparing it by identity would defeat the memo.
  const search = searchParams.toString();

  const state = useMemo(() => {
    const params = new URLSearchParams(search);
    const next = {} as UrlState<K>;
    keys.forEach((key) => {
      const value = params.get(key);
      next[key] = value ?? defaults[key];
    });
    return next;
    // `keys` and `defaults` are literals at the call site; `search` is the
    // only value that actually varies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setState = useCallback(
    (patch: UrlState<K>, patchOptions?: { replace?: boolean }) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          Object.entries(patch).forEach(([key, value]) => {
            const v = value as string | undefined;
            if (v === undefined || v === '' || v === defaults[key as K]) {
              next.delete(key);
            } else {
              next.set(key, v);
            }
          });

          // A filter change makes the current page number meaningless — page 7
          // of the old result set is not page 7 of the new one.
          const touchedOther = Object.keys(patch).some(
            (key) => !resetOnChange.includes(key as K),
          );
          if (touchedOther) {
            resetOnChange.forEach((key) => {
              if (!(key in patch)) next.delete(key);
            });
          }

          return next;
        },
        { replace: patchOptions?.replace ?? replace },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, replace],
  );

  /** Drops every key this hook owns, leaving unrelated params untouched. */
  const clear = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        keys.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  return { state, setState, clear };
}
